/**
 * HistoryDrawer — Xero-style timeline of "who did what" on one document.
 *
 * Shamil 2026-06-24: wants a button in the bottom-right of any transaction
 * that, when clicked, expands a list showing creator, edits, attachment
 * adds/removes, status changes, etc.
 *
 * Usage:
 *   <HistoryDrawer entityType="invoice" entityId={invoice.id} />
 *
 * Renders a fixed-position toggle pill at the bottom right of the viewport.
 * Click → expands an absolutely-positioned panel; click anywhere outside or
 * the close button to dismiss.
 *
 * Pulls /v1/history/:entityType/:entityId only when opened (lazy).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { historyApi, type HistoryEvent } from "../lib/business";

interface Props {
  entityType: "invoice" | "bill" | "payment" | "customer" | "supplier";
  entityId: string;
}

const actionLabel: Record<string, string> = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  void: "Voided",
  send: "Sent",
  approve: "Approved",
  reconcile: "Reconciled",
};

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
}

function describe(e: HistoryEvent): string {
  if (e.action === "create") return actionLabel[e.action];
  // Surface attachment-add / -remove crumbs we set in the attachments router.
  const after = e.after as Record<string, unknown> | null;
  const before = e.before as Record<string, unknown> | null;
  if (after && typeof after["attachment_added"] === "string") {
    return `Attached: ${after["attachment_added"]}`;
  }
  if (before && typeof before["attachment_removed"] === "string") {
    return `Removed attachment: ${before["attachment_removed"]}`;
  }
  return actionLabel[e.action] ?? e.action;
}

export function HistoryDrawer({ entityType, entityId }: Props) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["history", entityType, entityId],
    queryFn: () => historyApi.list(entityType, entityId),
    enabled: open,
    refetchOnMount: true,
  });

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {open && (
        <div className="absolute bottom-12 right-0 w-96 max-h-[60vh] overflow-y-auto rounded-lg border border-stone-300 bg-white shadow-xl">
          <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
            <span className="text-sm font-semibold text-stone-700">History</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-stone-500 hover:text-stone-800"
              aria-label="Close history"
            >
              ×
            </button>
          </div>
          <div className="divide-y divide-stone-100">
            {q.isLoading && <div className="p-4 text-xs text-stone-500">Loading…</div>}
            {q.isError && <div className="p-4 text-xs text-rose-600">Couldn't load history.</div>}
            {q.data?.events.length === 0 && (
              <div className="p-4 text-xs text-stone-500 italic">No history yet.</div>
            )}
            {q.data?.events.map((e) => (
              <div key={e.id} className="px-4 py-2 text-xs">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-stone-800">{describe(e)}</span>
                  <span className="text-stone-400">{relativeTime(e.occurred_at)}</span>
                </div>
                <div className="text-stone-500">
                  {e.user_name || e.user_email || "system"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 shadow-md hover:bg-stone-50"
      >
        History
      </button>
    </div>
  );
}
